import type { RecurringItem } from '@/api/recurring'
import { Badge } from '@/components/ui/badge'
import { useFormatCurrency } from '@/hooks/useFormatCurrency'
import { useIsReadOnly } from '@/hooks/useIsReadOnly'
import { cn } from '@/lib/utils'
import { cardSubline, dueBadge } from './dueLabel'
import { MarkDoneControl } from './MarkDoneControl'
import { useRecurringActions } from './useRecurringActions'
import { approxAmount } from '@/lib/recurring'

function DueCard({ item, onOpen }: { item: RecurringItem; onOpen: (item: RecurringItem) => void }) {
  const { format } = useFormatCurrency()
  const isReadOnly = useIsReadOnly()
  const actions = useRecurringActions(item)
  const badge = dueBadge(item.nextDueDate)
  const late = badge?.tone === 'error'
  const sub = cardSubline(item)
  return (
    <div
      onClick={() => onOpen(item)}
      className={cn(
        'min-w-[200px] cursor-pointer rounded-2xl border bg-[var(--fl-surface)] p-3 text-[13px] shadow-[var(--card-shadow-sm)] md:min-w-0 md:px-3.5',
        late ? 'border-[var(--fl-error-bg)]' : 'border-[var(--fl-border)]'
      )}
    >
      {badge && <Badge tone={badge.tone}>{badge.text}</Badge>}
      <p className="mt-1.5 truncate text-sm font-semibold text-[var(--fl-text-1)]">{item.name}</p>
      {sub && <p className="truncate text-xs text-[var(--fl-text-2)]">{sub}</p>}
      <div className="mt-2.5 flex flex-wrap items-center justify-between gap-2">
        <span
          className={cn(
            'whitespace-nowrap font-semibold tabular-nums',
            item.type === 'INCOME' ? 'text-[var(--fl-income)]' : 'text-[var(--fl-text-1)]'
          )}
        >
          {item.type === 'INCOME' && '+'}
          {approxAmount(item, format(item.amount))}
        </span>
        {!isReadOnly && (
          <MarkDoneControl
            item={item}
            actions={actions}
            label={item.type === 'INCOME' ? 'Got it' : 'Mark paid'}
            variant={late ? 'primary' : 'outline'}
          />
        )}
      </div>
      {actions.dialogs}
    </div>
  )
}

/** Late and due-within-a-week items, already filtered and sorted by the caller. */
export function DueThisWeekStrip({
  items,
  onOpen,
}: {
  items: RecurringItem[]
  onOpen: (item: RecurringItem) => void
}) {
  const { formatWhole } = useFormatCurrency()
  if (items.length === 0) return null
  const out = items.filter((i) => i.type === 'EXPENSE').reduce((s, i) => s + i.amount, 0)
  const income = items.filter((i) => i.type === 'INCOME').reduce((s, i) => s + i.amount, 0)
  return (
    <section aria-label="Due this week" className="mt-5">
      <div className="mb-2 flex items-baseline justify-between px-0.5">
        <h2 className="text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--fl-text-2)]">
          Due this week · {items.length}
        </h2>
        <p className="text-xs tabular-nums">
          {out > 0 && <span className="text-[var(--fl-error)]">{formatWhole(out)} out</span>}
          {income > 0 && (
            <span className="text-[var(--fl-income)]">
              {out > 0 ? ' ' : ''}
              {out > 0 ? `· ${formatWhole(income)} in` : `${formatWhole(income)} in`}
            </span>
          )}
        </p>
      </div>
      <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 md:mx-0 md:grid md:grid-cols-4 md:gap-2.5 md:overflow-visible md:px-0">
        {items.map((item) => (
          <DueCard key={item.id} item={item} onOpen={onOpen} />
        ))}
      </div>
    </section>
  )
}
